/**
 * Which actions of a toolbar move into its More (…) menu when the bar is too narrow (fixes after rc.9 testing).
 * The least important actions go first; the menu button then needs room of its own.
 */

export interface BarMeasure {
  key: string;
  /** Width in the bar, in pixels; 0 for an action that draws nothing (it takes no room and no gap). */
  width: number;
  /** Higher stays in the bar longer. */
  priority: number;
}

/**
 * The keys of the items that do not fit in `available` pixels, in bar order. With every item shown no menu button
 * is needed; once one item moves, the menu button (`moreWidth`, plus a gap) must fit too. Among equal priorities
 * the item further right moves first.
 */
export function overflowItems(items: BarMeasure[], available: number, moreWidth: number, gap: number): string[] {
  const used = (list: BarMeasure[]) => {
    const drawn = list.filter((i) => i.width > 0);
    return drawn.reduce((n, i) => n + i.width, 0) + gap * Math.max(0, drawn.length - 1);
  };
  // A pixel of slack absorbs sub-pixel rounding.
  if (used(items) <= available + 1) return [];
  const order = items.map((item, index) => ({ item, index })).sort((a, b) => a.item.priority - b.item.priority || b.index - a.index);
  const hidden = new Set<string>();
  for (const { item } of order) {
    hidden.add(item.key);
    const shown = items.filter((i) => !hidden.has(i.key));
    const drawn = shown.some((i) => i.width > 0);
    if (used(shown) + (drawn ? gap : 0) + moreWidth <= available + 1) break;
  }
  return items.filter((i) => hidden.has(i.key)).map((i) => i.key);
}

export const sameKeys = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((k, i) => k === b[i]);
